import { Sine, TimelineLite } from "gsap";
import * as HLF from "html-living-framework";
import { AppProgress } from "../Data/AppProgress";
import * as Constants from "../Data/Constants";
import { Regions, RegionType } from "../Data/Regions";
import * as Topics from "../Data/Topics";
import { Button } from "../GameObjects/Button";
import { DebugPhysicsRenderer } from "../GameObjects/DebugPhysicsRenderer";
import { UI, UIConfiguration } from "../GameObjects/UI";
import { World } from "../GameObjects/World";

export class Game extends HLF.State {
    private _isOnboarding: boolean;

    private _world: World;
    private _ui: UI;
    private _overlay: PIXI.Sprite;

    private _tempButton: Button;
    private _physicsRenderer: DebugPhysicsRenderer;
    private _tempButtonPressed: boolean;

    private _overlayTween: TimelineLite | undefined;
    private _subscriptions: any[];

    constructor() {
        super();
        this._isOnboarding = AppProgress.onboardingComplete === false;
        this._tempButtonPressed = false;

        // TODO:SH: Creating UI early to prevent interference with default values and debug controls.
        this._ui = new UI(this._isOnboarding ? UIConfiguration.ONBOARDING : UIConfiguration.GAMEPLAY);

        this._world = new World();
        this.addChild(this._world);

        this.addChild(this._ui);

        this._tempButton = new Button(
            this.onTempButtonPressed.bind(this),
            "button-play", Constants.SPRITESHEET_UI,
        );
        this._tempButton.scale.set(0.4);
        this.addChild(this._tempButton);

        this._overlay = HLF.PixiUtils.makeSprite("white-pixel", Constants.SPRITESHEET_UI);
        this._overlay.tint = 0x000000;
        this._overlay.scale.set(this._size.x, this._size.y);
        this.addChild(this._overlay);

        this._physicsRenderer = new DebugPhysicsRenderer();
        this._physicsRenderer.visible = false;
        this.addChild(this._physicsRenderer);

        this._subscriptions = new Array<any>();
        this._subscriptions.push(PubSub.subscribe(Topics.SHOW_DEBUG, () => { this.toggleDebug(true); }));
        this._subscriptions.push(PubSub.subscribe(Topics.HIDE_DEBUG, () => { this.toggleDebug(false); }));
        this._subscriptions.push(PubSub.subscribe(Topics.HEALTH_DEPLETED, this.onHealthDepleted.bind(this)));
        this._subscriptions.push(PubSub.subscribe(Topics.LEVEL_COMPLETED, this.onLevelCompleted.bind(this)));
    }

    public static getRequiredAssets(): string[] {
        const requiredAssets: string[] = [
            "sky" + HLF.ResourceUtils.FILE_EXTENSION_JPG,
            Constants.SPRITESHEET_GAMEPLAY + HLF.ResourceUtils.FILE_EXTENSION_JSON,
            Constants.SPRITESHEET_TERRAIN_PREFIX + "common" + HLF.ResourceUtils.FILE_EXTENSION_JSON,
        ];
        requiredAssets.push(...Regions.activeData.requiredAssets);
        return requiredAssets;
    }

    public init(pSize: PIXI.Point): void {
        super.init(pSize);
        this._world.init(pSize);
    }

    public update(pDeltaTime: number): void {
        super.update(pDeltaTime);
        this._world.update(pDeltaTime);
        this._ui.update(pDeltaTime);
        if (this._physicsRenderer.visible) {
            this._physicsRenderer.render(this._world);
        }
    }

    public onResize(pSize: PIXI.Point): void {
        super.onResize(pSize);
        this._world.onResize(pSize);
        this._ui.onResize(pSize);

        this._overlay.scale.set(this._size.x, this._size.y);
        this._tempButton.position.set(this._size.x * 0.5 - this._tempButton.width, 0);
    }

    public animateIn(pOnComplete: () => void): void {
        pOnComplete();
        if (this._overlayTween !== undefined) { this._overlayTween.kill(); }
        if (AppProgress.onboardingComplete) {
            this._overlay.alpha = 1;
            this._overlayTween = new TimelineLite();
            this._overlayTween.to(this._overlay, 0.75, {
                alpha: 0,
                ease: Sine.easeInOut,
            }, 0.25);
        }
        else {
            // Don't display overlay when transitioning from splash screen
            this._overlay.alpha = 0;
        }
    }

    public animateOut(pOnComplete: () => void): void {
        if (this._overlayTween !== undefined) { this._overlayTween.kill(); }
        this._overlayTween = new TimelineLite();
        this._overlayTween.to(this._overlay, 0.75, {
            alpha: 1,
            ease: Sine.easeInOut,
        });
        this._overlayTween.call(pOnComplete);
    }

    public destroy(pOptions: boolean | PIXI.DestroyOptions = { children: true }): void {
        super.destroy(pOptions);
        this._physicsRenderer.visible = false;
        if (this._overlayTween !== undefined) { this._overlayTween.kill(); }
        for (let i = 0; i < this._subscriptions.length; ++i) {
            PubSub.unsubscribe(this._subscriptions[i]);
        }
    }

    private onHealthDepleted(): void {
        AppProgress.currentLevelCompleted = false;
        this.transitionTo(Constants.STATE_RESULTS, 0.75);
    }

    private onLevelCompleted(): void {
        if (this._isOnboarding) {
            AppProgress.onboardingComplete = true;
        }
        else {
            AppProgress.currentLevelCompleted = true;
        }
        this.transitionTo(this._isOnboarding ? Constants.STATE_GAME : Constants.STATE_RESULTS);
    }

    private toggleDebug(pValue: boolean): void {
        this._physicsRenderer.visible = pValue;
    }

    private transitionTo(pState: string, pDelay: number = 0): void {
        new TimelineLite().call(() => {
            PubSub.publishSync(HLF.STATE_TRANSITION, new HLF.StateToken(
                pState,
                Constants.LOAD_INTERSTITIAL,
            ));
        }, undefined, this, pDelay);
    }

    private onTempButtonPressed(): void {
        if (this._tempButtonPressed === false) {
            this._tempButtonPressed = true;
            this.onLevelCompleted();
        }
        else {
            this._tempButton.interactive = false;
            AppProgress.levelsCompleted = 7;
        }
    }
}
